/**
 * Predictive Search
 * Handles search input with live predictive results
 * Used by: nav-desktop-search.liquid, nav-search-fullpage.liquid
 * 
 * UI Templates: snippets/nav-predictive-search-templates.liquid
 * - All result templates are defined as <template> elements
 * - This file clones and populates those templates dynamically
 */

(function () {
  // Predictive Search functionality
  class PredictiveSearch {
    constructor(form) {
      this.form = form;
      this.input = form.querySelector('[data-search-input]');
      this.clearBtn = form.querySelector('[data-search-clear]');
      // Look for dropdown inside form first, then in parent element
      this.dropdown =
        form.querySelector('[data-predictive-search]') ||
        form.parentElement.querySelector('[data-predictive-search]');

      console.log('PredictiveSearch init:', {
        form: form,
        input: this.input,
        dropdown: this.dropdown,
        searchTypes: form.dataset.searchTypes,
        searchLimit: form.dataset.searchLimit
      });

      this.results = this.dropdown ? this.dropdown.querySelector('[data-predictive-results]') : null;

      if (!this.dropdown || !this.results) {
        console.warn('Predictive search dropdown not found for form:', form);
      }

      // Get search configuration from data attributes (for predictive only)
      this.searchTypes = form.dataset.searchTypes || 'product,page,article,collection';
      this.searchLimit = form.dataset.searchLimit || '6';
      this.allowedBlogs = form.dataset.allowedBlogs ? form.dataset.allowedBlogs.split(',') : null;
      this.collectionTypeFilter = form.dataset.collectionTypeFilter || null;

      this.timeout = null;

      // Always init: clear button + input listener (clear shows whenever input has value)
      this.init();
    }

    init() {
      if (!this.input) return;

      // Show/hide clear button based on input value (always, even without predictive)
      this.toggleClearButton();

      this.input.addEventListener('input', () => {
        clearTimeout(this.timeout);
        const query = this.input.value.trim();

        this.toggleClearButton();

        if (!this.dropdown) return;

        if (query.length < 2) {
          this.hideDropdown();
          return;
        }

        this.timeout = setTimeout(() => {
          this.search(query);
        }, 300);
      });

      if (this.clearBtn) {
        this.clearBtn.addEventListener('click', () => {
          this.input.value = '';
          this.input.blur();
          this.toggleClearButton();
          this.hideDropdown();
        });
      }

      if (!this.dropdown) return;

      document.addEventListener('click', (e) => {
        if (!this.form.parentElement.contains(e.target)) {
          this.hideDropdown();
        }
      });

      this.input.addEventListener('keydown', (e) => {
        if (e.key === 'Escape') {
          this.hideDropdown();
        }
      });
    }

    toggleClearButton() {
      if (!this.clearBtn) return;

      if (this.input.value.length > 0) {
        this.clearBtn.classList.add('is-visible');
        this.clearBtn.setAttribute('aria-hidden', 'false');
      } else {
        this.clearBtn.classList.remove('is-visible');
        this.clearBtn.setAttribute('aria-hidden', 'true');
      }
    }

    async search(query) {
      try {
        this.showLoading();

        // Use Shopify's predictive search API with configurable types and limit
        const response = await fetch(
          `/search/suggest.json?q=${encodeURIComponent(query)}&resources[type]=${this.searchTypes}&resources[limit]=${
            this.searchLimit
          }`
        );

        if (!response.ok) throw new Error('Search failed');

        const data = await response.json();
        console.log('Search response:', data);

        // Handle the response structure correctly
        const results = data.resources ? data.resources.results : data;
        this.displayResults(results);
      } catch (error) {
        console.error('Predictive search error:', error);
        this.hideDropdown();
      }
    }

    // Helper: Clone and populate template
    cloneTemplate(templateId) {
      const template = document.getElementById(templateId);
      if (!template) {
        console.error(`Template ${templateId} not found`);
        return null;
      }
      return template.content.cloneNode(true);
    }

    // Helper: Get icon from library
    getIcon(iconName) {
      const library = document.getElementById('search-icon-library');
      if (!library) {
        console.error('Icon library not found');
        return '';
      }

      const iconContainer = library.querySelector(`[data-icon="${iconName}"]`);
      if (!iconContainer) {
        console.warn(`Icon "${iconName}" not found in library`);
        return '';
      }

      // Clone the icon HTML and add margin
      const iconClone = iconContainer.cloneNode(true);
      const svg = iconClone.querySelector('svg');
      if (svg) {
        svg.style.marginRight = '8px';
      }

      return iconClone.innerHTML;
    }

    // Helper: Create category header
    createCategoryHeader(label, count, searchUrl, showMoreLink = false) {
      const header = this.cloneTemplate('search-category-header');
      if (!header) return '';

      const labelSpan = header.querySelector('.rr-search-category-label');
      const countSpan = header.querySelector('.rr-search-category-count');
      const moreLinkElement = header.querySelector('.rr-search-category-more');

      labelSpan.textContent = label;
      countSpan.textContent = `(${count})`;
      
      if (moreLinkElement) {
        if (showMoreLink && searchUrl) {
          moreLinkElement.href = searchUrl;
        } else {
          moreLinkElement.remove();
        }
      }

      return header;
    }

    // Helper: Create product item
    createProductItem(product) {
      const item = this.cloneTemplate('search-product-item');
      if (!item) return null;

      const link = item.querySelector('.rr-search-result-item');
      const img = item.querySelector('.rr-search-result-image');
      const title = item.querySelector('.rr-search-result-title');
      const metaText = item.querySelector('.rr-search-result-meta-text');
      const price = item.querySelector('.rr-search-result-price');

      link.href = product.url;
      title.textContent = product.title;

      // Set product image or placeholder
      if (product.image) {
        img.src = product.image;
        img.alt = product.title;
      } else {
        img.src = '/cdn/shop/files/img-default-product-image.svg';
        img.alt = product.title;
      }

      // Set product type as metadata (API returns 'type' not 'product_type')
      const productType = product.type || 'Equipment';
      metaText.textContent = productType;

      // Price label. Same wording as snippets/product-price (keep the two in step):
      //   $0                        -> "Request a quote"
      //   rental                    -> "Starting at $X / day"
      //   sale only, used           -> "Buy used $X"
      //   sale only (new) / consumable -> "$X / unit"
      // Rentals have several day prices, so a single price means a sale item
      // or a single-variant rental; those are checked against
      // /products/<handle>.js, whose variant titles say "Rent" or "Used".
      if (product.price_min !== undefined && product.price_min !== null) {
        // Parse price (API returns as string like "60.00")
        const minPrice = parseFloat(product.price_min);
        const maxPrice = parseFloat(product.price_max);

        if (minPrice === 0) {
          price.textContent = 'Request a quote';
          price.classList.add('rr-search-result-price--contact');
        } else if (minPrice !== maxPrice) {
          price.textContent = `Starting at ${this.formatPrice(minPrice)} / day`;
        } else {
          price.textContent = `${this.formatPrice(minPrice)} / unit`;
          this.refinePriceLabel(product, price, minPrice);
        }
      } else {
        price.classList.add('d-none');
      }

      return item;
    }

    // Helper: "$4,750.00", matching the money format used on the site
    formatPrice(amount) {
      return `$${amount.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
    }

    // Helper: relabel a single-price product from its variant titles
    async refinePriceLabel(product, priceEl, amount) {
      try {
        const response = await fetch(`${product.url.split('?')[0]}.js`);
        if (!response.ok) return;
        const data = await response.json();
        const titles = (data.variants || []).map((v) => (v.title || '').toLowerCase());
        if (titles.some((t) => t.includes('rent'))) {
          priceEl.textContent = `Starting at ${this.formatPrice(amount)} / day`;
        } else if (titles.some((t) => t.includes('used'))) {
          priceEl.textContent = `Buy used ${this.formatPrice(amount)}`;
        }
      } catch (error) {
        // Keep the "/ unit" label
      }
    }

    // Helper: Create article item (training videos, webinars)
    createArticleItem(article, blogName) {
      const item = this.cloneTemplate('search-article-item');
      if (!item) return null;

      const link = item.querySelector('.rr-search-result-item');
      const title = item.querySelector('.rr-search-result-title');
      const dateText = item.querySelector('.rr-search-result-date-text');
      const blogChip = item.querySelector('.rr-search-result-chip');

      link.href = article.url;
      title.textContent = article.title;

      // Set blog chip text
      if (blogChip && blogName) {
        blogChip.textContent = blogName;
      }

      // Format and display published date
      if (article.published_at) {
        const date = new Date(article.published_at);
        const options = { year: 'numeric', month: 'short', day: 'numeric' };
        dateText.textContent = date.toLocaleDateString('en-US', options);
      }

      return item;
    }

    // Helper: Create simple item (collection, page)
    createSimpleItem(item, displayCleanName = false) {
      const element = this.cloneTemplate('search-simple-item');
      if (!element) return null;

      const link = element.querySelector('.rr-search-result-item');
      const title = element.querySelector('.rr-search-result-title');

      link.href = item.url;
      
      // If displayCleanName is true and title has " ~ ", show only the name part
      if (displayCleanName && item.title.includes(' ~ ')) {
        const parts = item.title.split(' ~ ');
        title.textContent = parts.slice(1).join(' ~ ').trim(); // Everything after first " ~ "
      } else {
        title.textContent = item.title;
      }

      return element;
    }

    // Helper: Filter collections by type prefix (split on " ~ ")
    filterCollectionsByType(collections) {
      if (!this.collectionTypeFilter || !collections || collections.length === 0) {
        return collections; // No filter specified or no collections to filter
      }

      const filtered = collections.filter((collection) => {
        // Split on " ~ " and check if first part matches filter
        const parts = collection.title.split(' ~ ');
        const collectionType = parts[0] ? parts[0].trim() : null;
        
        console.log(`Collection "${collection.title}" type: "${collectionType}", Filter: "${this.collectionTypeFilter}"`);
        
        return collectionType === this.collectionTypeFilter;
      });

      console.log(`Filtered ${filtered.length} of ${collections.length} collections for type "${this.collectionTypeFilter}"`);
      return filtered;
    }

    showLoading() {
      if (!this.results || !this.dropdown) return;

      const loading = this.cloneTemplate('search-loading');
      if (loading) {
        this.results.innerHTML = '';
        this.results.appendChild(loading);
      }
      this.dropdown.classList.remove('d-none');
      this.dropdown.classList.add('is-active');
      this.input.classList.add('has-dropdown-active');
    }

    displayResults(results) {
      if (!this.results || !this.dropdown) return;

      console.log('Display results called with:', results);
      
      // Log collection data to see what's available
      if (results?.collections) {
        console.log('Raw collection results:', results.collections);
        console.log('First collection object:', results.collections[0]);
      }

      // Check if we have any results
      const hasProducts = results?.products && results.products.length > 0;
      const hasPages = results?.pages && results.pages.length > 0;
      const hasCollections = results?.collections && results.collections.length > 0;

      // Filter articles to only include allowed blogs (if specified)
      const filteredArticles = this.allowedBlogs
        ? results?.articles?.filter((article) => {
            const urlParts = article.url.split('/');
            const blogIndex = urlParts.indexOf('blogs');
            return blogIndex !== -1 && this.allowedBlogs.includes(urlParts[blogIndex + 1]);
          }) || []
        : results?.articles || [];
      const hasArticles = filteredArticles.length > 0;

      // Filter collections by type prefix (if filter specified)
      const filteredCollections = hasCollections && this.collectionTypeFilter
        ? this.filterCollectionsByType(results.collections)
        : results?.collections || [];
      const hasFilteredCollections = filteredCollections.length > 0;

      if (!hasProducts && !hasPages && !hasArticles && !hasFilteredCollections) {
        console.log('No results found for any category');
        const noResults = this.cloneTemplate('search-no-results');
        if (noResults) {
        this.results.innerHTML = '';
        this.results.appendChild(noResults);
      }
      this.dropdown.classList.add('is-active');
      this.input.classList.add('has-dropdown-active');
      return;
    }

    // Build results using templates
    const fragment = document.createDocumentFragment();
      
      // Get current search query for "Show More" links
      const searchQuery = encodeURIComponent(this.input.value.trim());

      // Products
      if (results.products?.length) {
        const productSearchUrl = `/search?q=${searchQuery}&type=product`;
        fragment.appendChild(this.createCategoryHeader('PRODUCTS', results.products.length, productSearchUrl, true));
        results.products.forEach((product) => {
          const item = this.createProductItem(product);
          if (item) fragment.appendChild(item);
        });
      }

      // Collections (filtered by type prefix if specified)
      if (filteredCollections.length > 0) {
        console.log('Rendering collections:', filteredCollections.length);
        const collectionSearchUrl = `/search?q=${searchQuery}&type=collection`;
        fragment.appendChild(this.createCategoryHeader('COLLECTIONS', filteredCollections.length, collectionSearchUrl, false));
        filteredCollections.forEach((collection) => {
          console.log('Creating item for collection:', collection.title);
          // Pass true to display clean name (without "Type ~ " prefix)
          const item = this.createSimpleItem(collection, true);
          if (item) fragment.appendChild(item);
        });
      }

      // Pages
      if (results.pages?.length) {
        const pageSearchUrl = `/search?q=${searchQuery}&type=page`;
        fragment.appendChild(this.createCategoryHeader('PAGES', results.pages.length, pageSearchUrl, false));
        results.pages.forEach((page) => {
          const item = this.createSimpleItem(page);
          if (item) fragment.appendChild(item);
        });
      }

      // Articles - Display all under "Training Materials" with blog chips
      if (filteredArticles.length > 0) {
        // Add main "Training Materials" header
        const trainingSearchUrl = `/search?q=${searchQuery}&type=article`;
        fragment.appendChild(this.createCategoryHeader('TRAINING MATERIALS', filteredArticles.length, trainingSearchUrl, false));

        // Define blog configs for mapping handles to display names
        const blogConfigs = {
          'training-videos': { name: 'Training Videos', icon: 'video' },
          'webinars': { name: 'Webinars', icon: 'users' },
          'insights-case-studies': { name: 'Case Studies', icon: 'clipboard-check' },
          'training': { name: 'Training', icon: 'video' },
        };

        // Sort all articles by published date descending (most recent first)
        filteredArticles.sort((a, b) => {
          const dateA = new Date(a.published_at);
          const dateB = new Date(b.published_at);
          return dateB - dateA; // Descending order
        });

        // Display all articles with their blog chip
        filteredArticles.forEach((article) => {
          // Extract blog handle from article URL
          const urlParts = article.url.split('/');
          const blogIndex = urlParts.indexOf('blogs');
          const blogHandle = blogIndex !== -1 ? urlParts[blogIndex + 1] : null;
          
          // Get blog name from config
          const blogName = blogHandle && blogConfigs[blogHandle] ? blogConfigs[blogHandle].name : 'Article';
          
          const item = this.createArticleItem(article, blogName);
          if (item) fragment.appendChild(item);
        });
      }

      this.results.innerHTML = '';
      this.results.appendChild(fragment);
      this.dropdown.classList.remove('d-none');
      this.dropdown.classList.add('is-active');
      this.input.classList.add('has-dropdown-active');
    }

    hideDropdown() {
      if (!this.dropdown) return;

      this.dropdown.classList.add('d-none');
      this.dropdown.classList.remove('is-active');
      this.input.classList.remove('has-dropdown-active');
    }
  }

  // Initialize predictive search for all search forms
  function initPredictiveSearch() {
    document.querySelectorAll('[data-search-form]').forEach((form) => {
      // Check if already initialized
      if (form.dataset.predictiveSearchInit) return;
      form.dataset.predictiveSearchInit = 'true';
      new PredictiveSearch(form);
    });
  }

  // Initialize on DOMContentLoaded
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initPredictiveSearch);
  } else {
    // DOM already loaded
    initPredictiveSearch();
  }

  // Also initialize on modal shown (for mobile search)
  document.addEventListener('shown.bs.modal', (e) => {
    if (e.target.id === 'searchModal') {
      initPredictiveSearch();
    }
  });

})();
